import { supabase } from '@/lib/supabaseClient';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface PromocionActiva {
  id_promocion: string;
  nombre: string;
  tipo: 'trial' | 'campana';
  duracion_dias: number | null;
  fecha_fin_fija: string | null; // 'YYYY-MM-DD' o null
  descuento_pct: number | null;
  descripcion: string | null; // puede contener el placeholder {{fecha}}
}

export interface EmpresaPromocionActiva {
  id_empresa: string;
  id_empresa_promocion: string;
  id_promocion: string;
  nombre_promocion: string;
  tipo_promocion: 'trial' | 'campana';
  fecha_inicio: string;       // fecha real en que se asignó a esta empresa
  fecha_fin: string | null;   // fecha real en que termina para esta empresa (NULL = indefinida)
  descuento_pct: number | null;
  id_cupon_stripe: string | null;
}

// ─── Fetch ────────────────────────────────────────────────────────────────────

/**
 * Devuelve la promoción que se está asignando automáticamente a las
 * empresas nuevas ahora mismo (lanzamiento, trial de 14 días, etc.),
 * o null si no hay ninguna activa en este momento.
 */
export async function cargarPromocionActiva(): Promise<PromocionActiva | null> {
  const { data, error } = await supabase
    .from('vista_promocion_registro_activa')
    .select('*')
    .maybeSingle();

  if (error) {
    console.error('Error al cargar la promoción activa:', error);
    return null;
  }

  return data;
}

/**
 * Devuelve la promoción realmente vigente para UNA empresa concreta
 * (empresa_promociones), con fechas ya resueltas — no la plantilla que se
 * usa para autoasignar en el registro. Úsala en pantallas autenticadas
 * como Suscripción; cargarPromocionActiva() es solo para páginas públicas
 * (pricing, login, registro) donde aún no hay empresa.
 */
export async function cargarPromocionEmpresa(
  idEmpresa: string
): Promise<EmpresaPromocionActiva | null> {
  const { data, error } = await supabase
    .from('vista_empresa_promocion_activa')
    .select('*')
    .eq('id_empresa', idEmpresa)
    .maybeSingle();

  if (error) {
    console.error('Error al cargar la promoción de la empresa:', error);
    return null;
  }

  return data;
}

// ─── Helper de formato ────────────────────────────────────────────────────────

/**
 * Construye el texto a mostrar en el frontend según el tipo de promo:
 * - fecha_fin_fija  → "Gratis hasta el 1 de enero de 2027 — Lanzamiento"
 * - duracion_dias   → "14 días de prueba gratis — Trial 14 días"
 * - ninguna de las dos (promo indefinida) → solo el nombre
 */
export function formatearTextoPromocion(promo: PromocionActiva | null): string | null {
  if (!promo) return null;

  if (promo.fecha_fin_fija) {
    const fecha = new Date(promo.fecha_fin_fija).toLocaleDateString('es-ES', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
    return `Gratis hasta el ${fecha} — ${promo.nombre}`;
  }

  if (promo.duracion_dias) {
    return `${promo.duracion_dias} días de prueba gratis — ${promo.nombre}`;
  }

  return promo.nombre;
}

/**
 * Devuelve la fecha/duración de la promo en texto, para sustituir el
 * placeholder {{fecha}} dentro de descripcion:
 * - fecha_fin_fija → "1 de enero de 2027"
 * - duracion_dias  → "14 días"
 * - ninguna        → null
 */
export function textoFechaPromocion(promo: PromocionActiva | null): string | null {
  if (!promo) return null;
  if (promo.fecha_fin_fija) {
    return new Date(promo.fecha_fin_fija).toLocaleDateString('es-ES', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  }
  if (promo.duracion_dias) {
    return `${promo.duracion_dias} días`;
  }
  return null;
}

/**
 * Devuelve descripcion con el placeholder {{fecha}} ya sustituido por el
 * dato real (fecha_fin_fija o duracion_dias), listo para pintar en el
 * frontend. Si la promo no tiene descripcion, devuelve null.
 */
export function formatearDescripcionPromocion(
  promo: PromocionActiva | null
): string | null {
  if (!promo?.descripcion) return null;

  const fecha = textoFechaPromocion(promo);
  return fecha
    ? promo.descripcion.replace('{{fecha}}', fecha)
    : promo.descripcion;
}

/**
 * Texto para mostrar en pantallas autenticadas, con las fechas reales de
 * la promo asignada a esta empresa (no una plantilla con placeholder).
 */
export function formatearTextoPromocionEmpresa(
  promo: EmpresaPromocionActiva | null
): string | null {
  if (!promo) return null;

  const fmt = (f: string) =>
    new Date(f).toLocaleDateString('es-ES', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });

  if (promo.fecha_fin) {
    return `${promo.nombre_promocion} — gratis hasta el ${fmt(promo.fecha_fin)}`;
  }
  return `${promo.nombre_promocion} — sin fecha de fin`;
}